import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/apiClient";
import { generateProject } from "../lib/studioJob";
import { useAuth } from "../context/AuthContext";
import { useSpeech } from "../hooks/useSpeech";
import { AiOrb } from "../components/AiOrb";
import { AppNav } from "../components/AppNav";
import { SaraLogo } from "../components/SaraLogo";
import { MobileMenuSheet } from "../components/MobileMenuSheet";
import { NotificationBell } from "../components/NotificationBell";
import { Search, BarChart3, Sparkles, Bot, Brain, MessageSquare, History, BrainCircuit, LayoutGrid, Settings, LogOut, Cast, Globe, FolderOpen, MoreHorizontal, SlidersHorizontal, TrendingUp, Eye } from "lucide-react";
import { VoiceControlBar } from "../components/VoiceControlBar";
import { SettingsDrawer } from "../components/SettingsDrawer";
import { SkillsDrawer } from "../components/SkillsDrawer";
import { HistoryDrawer } from "../components/HistoryDrawer";
import { MemoryDrawer } from "../components/MemoryDrawer";
import { BrowserPreview } from "../components/BrowserPreview";
import { LinksBar } from "../components/LinksBar";
import { isInstantStop } from "../lib/voiceCommands";
import { toast } from "sonner";

let mid = 0;
const nid = () => `local-${mid++}-${Date.now()}`;
let greetScheduled = false; // garde au niveau module : survit aux remounts d'une même session

// Device IANA timezone (e.g. "Europe/Paris"), sent so Sara reasons in local time.
const deviceTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

// Salut vocal contextualisé selon l'heure RÉELLE de Paris (Europe/Paris), prononcé à l'ouverture de session.
function parisGreeting(firstName) {
  let weekday = "", hour = 9;
  try {
    const parts = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", hour: "numeric", hour12: false }).formatToParts(new Date());
    weekday = parts.find((p) => p.type === "weekday")?.value || "";
    hour = parseInt(parts.find((p) => p.type === "hour")?.value || "9", 10) % 24;
  } catch {
    hour = new Date().getHours();
  }
  const nm = firstName ? ` ${firstName}` : "";
  if (hour >= 5 && hour < 12) return `Bonjour${nm}, nous sommes ${weekday} matin. Comment puis-je vous aider ?`;
  if (hour >= 12 && hour < 18) return `Bon après-midi${nm}, nous sommes ${weekday}. Comment puis-je vous aider ?`;
  if (hour >= 18 && hour < 22) return `Bonsoir${nm}, nous sommes ${weekday} soir. Comment puis-je vous aider ?`;
  return `Bonsoir${nm}, il est tard ce ${weekday}. Comment puis-je vous aider ?`;
}

// Lightweight emotion cue from Sara's reply so the avatar expression is alive now.
// (Lot 2 « reconnaissance émotionnelle » will feed a richer backend-detected emotion here.)
function inferEmotion(text) {
  const raw = text || "";
  const t = raw.toLowerCase();
  const exclam = (raw.match(/!/g) || []).length;
  const positive = /\b(super|génial|genial|parfait|excellent|bravo|félicitations|felicitations|ravie?|contente?|heureuse|adore|aime|plaisir|cool|top|magnifique|merveilleux|formidable|fantastique|superbe|chouette|hâte|impatiente|sourire|youpi|yes)\b/.test(t);
  if (/[😊😁😄🙂😍👍🎉❤️✨🥳]/.test(raw) || (exclam >= 1 && positive) || exclam >= 2)
    return "happy";
  if (/\b(wow|oh là là|incroyable|étonnant|etonnant|surprise|impressionnant|waouh|dingue)\b/.test(t))
    return "surprised";
  return "neutral";
}

const COLOR_WORDS = {
  cyan: 185, bleu: 210, azur: 210, or: 43, doré: 43, jaune: 50, vert: 144,
  émeraude: 144, emeraude: 144, rouge: 345, cramoisi: 345, violet: 270,
  mauve: 275, rose: 320, orange: 25, turquoise: 175,
};

function stripWake(text, wake) {
  const w = (wake || "Sara").trim().toLowerCase();
  const esc = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const t = text.trim();
  const re = new RegExp(`^(?:h[eé]?y?\\s+|ok\\s+|dis\\s+|eh\\s+|coucou\\s+)?${esc}\\b[\\s,:.!?-]*`, "i");
  if (re.test(t)) return t.replace(re, "").trim();
  const idx = t.toLowerCase().indexOf(w);
  if (idx !== -1 && idx <= 14) {
    return (t.slice(0, idx) + t.slice(idx + w.length)).replace(/^[\s,:.!?-]+/, "").trim();
  }
  return null;
}

export default function Assistant() {
  const { user, updateSettings, logout } = useAuth();
  const navigate = useNavigate();
  const {
    voices,
    listening,
    speaking,
    interim,
    micReady,
    micLevelRef,
    speakLevelRef,
    recogSupported,
    recognitionBroken,
    enableMic,
    startListening,
    stopListening,
    setRecogLang,
    setSilenceMs,
    whisperRecording,
    startWhisper,
    stopWhisper,
    speak,
    stopSpeaking,
    unlockAudio,
  } = useSpeech();

  const [transcript, setTranscript] = useState([]);
  const [memories, setMemories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [handsFree, setHandsFree] = useState(true);
  const [openedLinks, setOpenedLinks] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [recognitionLang, setRecognitionLang] = useState("fr-FR");
  const [avatarEmotion, setAvatarEmotion] = useState("neutral");
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [pendingLabel, setPendingLabel] = useState("");

  // Image generation settings + pending image (for retouch/edit)
  const [imgSize, setImgSize] = useState(() => localStorage.getItem("sara_img_size") || "1K");
  const [imgRatio, setImgRatio] = useState(() => localStorage.getItem("sara_img_ratio") || "1:1");
  const [pendingImage, setPendingImage] = useState(null);
  useEffect(() => { localStorage.setItem("sara_img_size", imgSize); }, [imgSize]);
  useEffect(() => { localStorage.setItem("sara_img_ratio", imgRatio); }, [imgRatio]);

  const chatAbortRef = useRef(null);
  const thinkTimerRef = useRef(null);
  const voiceTurnRef = useRef(false);

  const speakReply = useCallback((text, spokenText) => {
    const toSpeak = (spokenText && spokenText.trim()) ? spokenText : text;
    speak(toSpeak, user?.voice);
  }, [speak, user]);

  const state = speaking ? "speaking" : busy ? "thinking" : listening ? "listening" : "idle";

  // reset the avatar expression to neutral once Sara is done speaking
  useEffect(() => {
    if (state === "idle") setAvatarEmotion("neutral");
  }, [state]);

  useEffect(() => {
    api
      .get("/chat/history")
      .then(({ data }) => setTranscript(data.map((m) => ({ id: m.id, role: m.role, text: m.text, images: m.images || [] }))))
      .catch(() => {});
    api
      .get("/memory")
      .then(({ data }) => setMemories(data))
      .catch(() => {});
  }, []);

  // Salut vocal d'ouverture de session (une seule fois par session, heure réelle de Paris).
  useEffect(() => {
    if (!user || greetScheduled) return;
    if (sessionStorage.getItem("sara_greeted") === "1") { greetScheduled = true; return; }
    greetScheduled = true;
    const firstName = (user.name || "").trim().split(/\s+/)[0] || "";
    const greeting = parisGreeting(firstName);
    // pas de clearTimeout au cleanup : un double-montage (StrictMode) ne doit pas annuler le salut
    setTimeout(() => {
      sessionStorage.setItem("sara_greeted", "1");
      try { unlockAudio(); } catch (_) {}
      speak(greeting, user.voice);
    }, 1000);
  }, [user, speak, unlockAudio]);

  // request the microphone once on entry so hands-free can begin
  useEffect(() => {
    if (!recogSupported) {
      setHandsFree(false);
      return;
    }
    enableMic().then((ok) => {
      if (!ok) {
        setHandsFree(false);
        toast.error("Micro indisponible — activez-le ou tapez vos commandes");
      }
    });
  }, [enableMic, recogSupported]);

  // apply the user's adjustable silence window (0.8s–2.0s) for auto-send
  useEffect(() => {
    if (user?.silence_ms) setSilenceMs(user.silence_ms);
  }, [user?.silence_ms, setSilenceMs]);

  // Push-to-talk (Whisper) est un SECOURS, activé uniquement quand ce navigateur ne
  // peut pas faire d'écoute continue (API absente, ou moteur bloqué type Brave), ou si
  // l'opérateur l'a explicitement choisi dans ses réglages. Sinon : écoute continue.
  const whisperDefault = !!user?.whisper_default || !recogSupported || recognitionBroken;
  useEffect(() => {
    if (whisperDefault) {
      setHandsFree(false);
      stopListening();
      if (recognitionBroken) {
        toast.message("Écoute continue indisponible sur ce navigateur — appuie sur le micro pour parler.");
      }
    }
  }, [whisperDefault, recognitionBroken, stopListening]);

  // Active le micro dès l'ouverture pour que Sara écoute tout de suite (les navigateurs
  // l'accordent silencieusement si l'autorisation a déjà été donnée ; sinon un premier
  // appui sur le bouton micro reste nécessaire — règle des navigateurs).
  const autoMicTriedRef = useRef(false);
  useEffect(() => {
    if (!handsFree || whisperDefault || micReady || autoMicTriedRef.current) return;
    autoMicTriedRef.current = true;
    enableMic();
  }, [handsFree, whisperDefault, micReady, enableMic]);

  // ---- execute Sara's <do>...</do> site actions ----
  const scrollTo = (target) => {
    const map = { memory: "memory-panel", answers: "intel-deck-panel" };
    if (target === "top") return window.scrollTo({ top: 0, behavior: "smooth" });
    if (target === "bottom") return window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    const el = document.querySelector(`[data-testid="${map[target]}"]`);
    el && el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const clearChat = useCallback(async () => {
    setTranscript([]);
    try {
      await api.delete("/chat/history");
    } catch (_) {}
  }, []);

  const openUrl = useCallback((url) => {
    let u = (url || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    let host = u;
    try {
      host = new URL(u).hostname.replace(/^www\./, "");
    } catch (_) {}
    setOpenedLinks((prev) =>
      [{ id: `${Date.now()}-${Math.random()}`, url: u, host, at: Date.now() }, ...prev.filter((l) => l.url !== u)].slice(0, 12)
    );
    let win = null;
    try {
      win = window.open(u, "_blank", "noopener,noreferrer");
    } catch (_) {}
    if (!win) {
      toast(`Lien prêt : ${host}`, {
        action: { label: "Ouvrir", onClick: () => window.open(u, "_blank", "noopener,noreferrer") },
        duration: 12000,
      });
    }
  }, []);

  const openLink = useCallback((u) => window.open(u, "_blank", "noopener,noreferrer"), []);

  const runAction = useCallback(
    (raw) => {
      const idx = raw.indexOf(":");
      const name = idx === -1 ? raw : raw.slice(0, idx);
      const arg = idx === -1 ? "" : raw.slice(idx + 1);
      switch (name) {
        case "open_settings":
          setSettingsOpen(true);
          break;
        case "open_skills":
          setSkillsOpen(true);
          break;
        case "close_panels":
          setSettingsOpen(false);
          setSkillsOpen(false);
          break;
        case "set_color": {
          let hue = parseInt(arg, 10);
          if (Number.isNaN(hue)) hue = COLOR_WORDS[(arg || "").trim().toLowerCase()];
          if (typeof hue === "number" && !Number.isNaN(hue)) updateSettings({ hue }).catch(() => {});
          break;
        }
        case "scroll":
          scrollTo((arg || "bottom").trim());
          break;
        case "clear_chat":
          clearChat();
          break;
        case "stop":
          stopSpeaking();
          break;
        case "open_url":
          openUrl(arg);
          break;
        case "open_trading":
          navigate("/trading");
          break;
        default:
          break;
      }
    },
    [updateSettings, clearChat, stopSpeaking, openUrl, navigate]
  );

  // ---- interrupt: stop audio + CANCEL the in-flight LLM call + re-listen (barge-in) ----
  const interrupt = useCallback(() => {
    if (thinkTimerRef.current) { clearTimeout(thinkTimerRef.current); thinkTimerRef.current = null; }
    try { chatAbortRef.current && chatAbortRef.current.abort(); } catch (_) {}
    chatAbortRef.current = null;
    stopSpeaking();
    setBusy(false);
    setAwaitingConfirm(false);
  }, [stopSpeaking]);

  const handleSend = useCallback(
    async (text, fromVoice = false) => {
      if (!text || busy) return;
      voiceTurnRef.current = fromVoice === true;
      unlockAudio();
      stopSpeaking();
      setAwaitingConfirm(false);

      setTranscript((prev) => [...prev, { id: nid(), role: "user", text }]);
      setBusy(true);
      const controller = new AbortController();
      chatAbortRef.current = controller;
      try {
        const { data } = await api.post(
          "/chat",
          { message: text, image_size: imgSize, image_ratio: imgRatio, client_tz: deviceTz() },
          { signal: controller.signal }
        );
        chatAbortRef.current = null;
        // extract + run site actions, strip tags before display/speech
        const actions = [...data.reply.matchAll(/<do>(.*?)<\/do>/gs)].map((m) => m[1].trim());
        const clean = data.reply.replace(/<do>.*?<\/do>/gs, "").trim();
        actions.forEach(runAction);
        setAvatarEmotion(data.emotion || inferEmotion(clean));
        setAwaitingConfirm(!!data.awaiting_confirmation);
        setPendingLabel(data.pending_label || "");

        const msgId = nid();
        setTranscript((prev) => [
          ...prev,
          { id: msgId, role: "assistant", text: clean, build: null, building: !!data.build_request, images: data.images || [] },
        ]);
        if (data.memories_saved?.length) {
          setMemories((prev) => [...data.memories_saved, ...prev]);
          toast.success(
            data.memories_saved.length === 1 ? "Nouvelle information mémorisée" : `${data.memories_saved.length} informations mémorisées`
          );
        }
        // brief "thinking" pause before Sara speaks (kept short for tight turn-taking);
        // stored in a ref so a barge-in can cancel it before she starts talking.
        const think = user?.hesitations === false ? 0 : 120 + Math.random() * 220;
        thinkTimerRef.current = window.setTimeout(() => {
          thinkTimerRef.current = null;
          setBusy(false);
          speakReply(clean, data.spoken_reply);
        }, think);

        // Sara navigated the web herself → surface the live browser preview
        if (data.browser_used || data.browser_active) setBrowserOpen(true);

        // Sara generated image(s) → surface them in the History drawer
        if (data.images?.length) {
          setHistoryOpen(true);
          toast.success(data.images.length === 1 ? "Image générée par Sara" : `${data.images.length} images générées`);
        }

        // Sara asked her Studio to build a site/app/software → generate it now
        if (data.build_request?.prompt) {
          setHistoryOpen(true);
          try {
            const proj = await generateProject({
              prompt: data.build_request.prompt,
              kind: "auto",
              projectId: data.build_request.project_id || null,
            });
            setTranscript((prev) =>
              prev.map((m) => (m.id === msgId ? { ...m, build: proj, building: false } : m))
            );
            toast.success(
              proj?.continued_from
                ? `Projet mis à jour (v${proj.version}) — rien n'a été perdu`
                : "Projet généré par Sara"
            );
          } catch (err) {
            setTranscript((prev) =>
              prev.map((m) => (m.id === msgId ? { ...m, building: false, buildError: true } : m))
            );
            toast.error(err?.message || "Échec de la génération du projet");
          }
        }
      } catch (e) {
        chatAbortRef.current = null;
        // user barged in / interrupted → request aborted: stay silent, go back to listening
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || api.isCancel?.(e)) {
          setBusy(false);
          return;
        }
        setBusy(false);
        const msg = "Erreur de liaison neuronale. Réessayez, je vous prie.";
        setTranscript((prev) => [...prev, { id: nid(), role: "assistant", text: msg }]);
        toast.error("Impossible de joindre Sara Connor");
      }
    },
    [busy, speakReply, stopSpeaking, user, runAction, unlockAudio, imgSize, imgRatio]
  );

  const analyzeFile = useCallback(
    async (file) => {
      if (!file || busy) return;
      unlockAudio();
      stopSpeaking();
      setTranscript((prev) => [...prev, { id: nid(), role: "user", text: `📎 ${file.name}` }]);
      setBusy(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/chat/attachment", form);
        setTranscript((prev) => [...prev, { id: nid(), role: "assistant", text: data.reply }]);
        setBusy(false);
        speakReply(data.reply);
      } catch (e) {
        setBusy(false);
        const msg = "Je n'ai pas pu lire ce fichier. Réessayez, je vous prie.";
        setTranscript((prev) => [...prev, { id: nid(), role: "assistant", text: msg }]);
        toast.error("Échec de l'envoi du fichier");
      }
    },
    [busy, speakReply, stopSpeaking, unlockAudio]
  );

  const handleSendFile = useCallback(
    (file) => {
      if (!file || busy) return;
      if (file.type?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => setPendingImage({ name: file.name, dataUrl: reader.result, file });
        reader.readAsDataURL(file);
      } else {
        analyzeFile(file);
      }
    },
    [busy, analyzeFile]
  );

  const analyzePendingImage = useCallback(() => {
    if (pendingImage?.file) {
      analyzeFile(pendingImage.file);
      setPendingImage(null);
    }
  }, [pendingImage, analyzeFile]);

  const editPendingImage = useCallback(
    async (instruction) => {
      if (!pendingImage) return;
      if (!instruction || !instruction.trim()) {
        toast.error("Écris une instruction de retouche");
        return;
      }
      unlockAudio();
      stopSpeaking();
      const img = pendingImage;
      setPendingImage(null);
      setTranscript((prev) => [...prev, { id: nid(), role: "user", text: `🖌️ ${instruction} (sur ${img.name})` }]);
      setBusy(true);
      try {
        const { data } = await api.post("/image/edit", {
          image_base64: img.dataUrl, prompt: instruction, size: imgSize, ratio: imgRatio,
        });
        setTranscript((prev) => [
          ...prev,
          { id: nid(), role: "assistant", text: "Voici l'image retouchée.", images: [{ url: data.url, prompt: instruction }] },
        ]);
        setBusy(false);
        setHistoryOpen(true);
        toast.success("Image retouchée par Sara");
        speakReply("Voici l'image retouchée.");
      } catch (e) {
        setBusy(false);
        toast.error(apiError(e, "Retouche impossible"));
      }
    },
    [pendingImage, imgSize, imgRatio, speakReply, stopSpeaking, unlockAudio]
  );

  // keep a ref to the latest handleSend for the recognition callback
  const handleSendRef = useRef(handleSend);
  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const onSpeechResult = useCallback(
    ({ final }) => {
      if (!final || !final.trim()) return;
      let text = final.trim();
      if (user?.wake_word_enabled) {
        const stripped = stripWake(text, user?.wake_word);
        if (stripped == null) return; // wake word absent — on ignore
        if (!stripped) {
          speakReply("Oui ?");
          return;
        }
        text = stripped;
      }
      // instant local command: "stop / arrête" → cut voice + running actions, no LLM call
      if (isInstantStop(text)) {
        interrupt();
        api.post("/voice/stop-all").catch(() => {});
        return;
      }
      stopListening();
      handleSendRef.current(text, true);
    },
    [stopListening, user, speakReply, interrupt]
  );

  // ---- hands-free loop: listen whenever idle, pause while thinking/speaking ----
  useEffect(() => {
    if (handsFree && !whisperDefault && micReady && recogSupported && !speaking && !busy && !whisperRecording) {
      startListening(onSpeechResult);
    } else {
      stopListening();
    }
  }, [handsFree, whisperDefault, micReady, recogSupported, speaking, busy, whisperRecording, startListening, stopListening, onSpeechResult]);

  // ---- barge-in: interrupt Sara the moment the operator speaks — while she is
  // SPEAKING *or* THINKING. State machine: arm only after the mic goes quiet (so the
  // tail of the operator's own utterance that triggered the send can't cancel it, and
  // Sara's echo-cancelled voice doesn't self-trigger), then fire on fresh speech.
  // Desktop only (mobile releases the mic during playback — documented limitation). ----
  useEffect(() => {
    // typed messages are never cancelled by mic noise while Sara is thinking
    const active = (speaking || (busy && voiceTurnRef.current)) && !whisperRecording;
    if (!active) return;
    let armed = false;
    let hi = 0;
    let raf;
    const check = () => {
      const lvl = micLevelRef.current || 0;
      if (!armed) {
        if (lvl < 0.12) armed = true; // operator's turn ended / room is quiet
      } else if (lvl > 0.3) {
        hi += 1;
        if (hi > 12) { interrupt(); return; } // ~200ms of sustained speech, not a click/cough
      } else {
        hi = Math.max(0, hi - 1);
      }
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, [speaking, busy, whisperRecording, micLevelRef, interrupt]);

  const toggleHandsFree = useCallback(async () => {
    unlockAudio();
    // Only "turn off" when the mic is actually live; otherwise (re)acquire it.
    if (handsFree && micReady) {
      setHandsFree(false);
      stopListening();
    } else {
      const ok = micReady || (await enableMic());
      if (ok) setHandsFree(true);
      else toast.error("Micro refusé par le navigateur");
    }
  }, [handsFree, micReady, enableMic, stopListening, unlockAudio]);

  const onSetLang = useCallback((lang) => {
    setRecognitionLang(lang);
    setRecogLang(lang);
  }, [setRecogLang]);

  const onWhisperToggle = useCallback(async () => {
    unlockAudio();
    if (!whisperRecording) {
      if (handsFree) stopListening();
      await startWhisper();
    } else {
      const data = await stopWhisper();
      if (data?.text) handleSend(data.text, true);
      if (data?.language_code) {
        setRecognitionLang(data.language_code);
        setRecogLang(data.language_code);
      }
    }
  }, [whisperRecording, handsFree, stopListening, startWhisper, stopWhisper, unlockAudio, handleSend, setRecogLang]);

  const deleteMemory = useCallback(async (id) => {
    setMemories((prev) => prev.filter((m) => m.id !== id));
    try {
      await api.delete(`/memory/${id}`);
    } catch (e) {
      toast.error("Échec de la suppression");
    }
  }, []);

  const aName = user?.assistant_name || "Sara";
  const stateLabel =
    state === "listening"
      ? `${aName.toUpperCase()} VOUS ÉCOUTE`
      : state === "thinking"
      ? `${aName.toUpperCase()} RÉFLÉCHIT`
      : state === "speaking"
      ? `${aName.toUpperCase()} VOUS RÉPOND`
      : "EN VEILLE — DITES QUELQUE CHOSE";
  const stateColor =
    state === "listening" ? "#00ffaa" : state === "thinking" ? "#ffaa00" : "var(--accent)";

  const lastMsg = transcript[transcript.length - 1];
  const caption = listening && interim ? interim : lastMsg?.text || "";
  const captionRole = listening && interim ? "user" : lastMsg?.role;

  const featureCards = [
    { id: "rechercher", label: "Rechercher", sub: "Sur le web en temps réel", icon: Search, onClick: () => setBrowserOpen(true) },
    { id: "analyser", label: "Analyser", sub: "Signaux, données & cryptos", icon: BarChart3, onClick: () => navigate("/trading") },
    { id: "creer", label: "Créer", sub: "Idées, contenus, solutions", icon: Sparkles, onClick: () => document.querySelector("[data-testid='assistant-text-input']")?.focus() },
    { id: "assister", label: "Vous assister", sub: "Au quotidien, tous vos projets", icon: Bot, onClick: () => document.querySelector("[data-testid='assistant-text-input']")?.focus() },
    { id: "apprendre", label: "Apprendre", sub: "Explorer de nouveaux horizons", icon: Brain, onClick: () => setMemoryOpen(true) },
    { id: "echanger", label: "Échanger", sub: "Conversation naturelle & fluide", icon: MessageSquare, onClick: () => toggleHandsFree() },
  ];

  const renderCard = (c) => (
    <button key={c.id} data-testid={`feature-${c.id}`} onClick={c.onClick} className="feature-card">
      <span className="feature-icon">
        <c.icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span>
        <span className="block text-sm font-semibold text-slate-100">{c.label}</span>
        <span className="block text-xs text-slate-400">{c.sub}</span>
      </span>
    </button>
  );

  const ibtn = (testid, onClick, Icon, title, extra = "") => (
    <button
      key={testid}
      data-testid={testid}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`h-9 w-9 items-center justify-center rounded-xl text-slate-400 hover:text-cyan-300 border border-cyan-500/10 hover:border-cyan-400/40 bg-white/[0.02] transition-colors ${extra || "flex"}`}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
    </button>
  );

  return (
    <div className="relative z-10 flex h-[100dvh] w-full overflow-hidden">
      <AppNav variant="sidebar" onOpenSettings={() => setSettingsOpen(true)} onOpenWeb={() => setBrowserOpen(true)} saraReady />

      <section className="flex flex-1 min-w-0 flex-col overflow-hidden">
        {/* header */}
        <header
          data-testid="assistant-header"
          className="shrink-0 h-16 px-4 sm:px-5 flex items-center justify-between gap-2 glass-topbar border-b"
        >
          <div className="flex items-center gap-2.5 md:invisible">
            <SaraLogo className="h-9 w-9" />
            <div className="leading-tight">
              <div className="font-jb text-sm font-bold tracking-[0.22em] text-white">SARA</div>
              <div className="font-jb text-[9px] tracking-[0.34em] text-cyan-400/80">CONNOR</div>
            </div>
          </div>

          {/* mobile: single settings button (matches mockup) */}
          <button
            data-testid="mobile-settings-button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Réglages"
            className="md:hidden h-11 w-11 flex items-center justify-center rounded-full glass-card text-cyan-300"
          >
            <SlidersHorizontal className="h-5 w-5" strokeWidth={1.75} />
          </button>

          {/* desktop: quick actions */}
          <div className="hidden md:flex items-center gap-1.5">
            {ibtn("cast-tv-button", () => window.open(`${window.location.origin}/tv`, "_blank", "noopener"), Cast, "Diffuser sur écran")}
            {ibtn("history-trigger-button", () => setHistoryOpen(true), History, "Historique")}
            {ibtn("memory-trigger-button", () => setMemoryOpen(true), BrainCircuit, "Mémoire")}
            {ibtn("skills-trigger-button", () => setSkillsOpen(true), LayoutGrid, "Modules")}
            <NotificationBell />
            {ibtn("settings-trigger-button", () => setSettingsOpen(true), Settings, "Réglages")}
            {ibtn("logout-button", logout, LogOut, "Déconnexion")}
          </div>
        </header>

        {/* content */}
        <main data-testid="assistant-main" className="relative flex-1 overflow-y-auto overflow-x-hidden">
          <div className="relative z-10 min-h-full flex flex-col items-center justify-center px-4 py-4 gap-4 lg:py-6 lg:gap-6">
            {/* greeting hero */}
            <div className="text-center animate-rise" style={{ animationDelay: "0.05s" }}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">
                Bonjour, je suis <span className="text-sara-gradient">SARA</span>
              </h1>
              <p className="mt-2 text-sm sm:text-base text-slate-400">Votre IA personnelle, toujours à vos côtés.</p>
            </div>

            {/* sphere flanked by feature cards on desktop */}
            <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-8">
              <div className="hidden lg:flex flex-col gap-3 items-end animate-rise" style={{ animationDelay: "0.4s" }}>{featureCards.slice(0, 3).map(renderCard)}</div>

              <div className="flex flex-col items-center animate-boot" style={{ animationDelay: "0.22s" }}>
                <div
                  data-testid="hud-status-badge"
                  className="mb-2 flex items-center gap-2 font-jb text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: stateColor }}
                >
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: stateColor }} />
                  {stateLabel}
                </div>
                <AiOrb
                  state={state}
                  micLevelRef={micLevelRef}
                  speakLevelRef={speakLevelRef}
                  className="w-[min(60vw,260px)] lg:w-[340px] aspect-square"
                />
              </div>

              <div className="hidden lg:flex flex-col gap-3 items-start animate-rise" style={{ animationDelay: "0.5s" }}>{featureCards.slice(3, 6).map(renderCard)}</div>
            </div>

            {/* mobile / tablet quick pills — Trading · Vision · Plus */}
            <div className="flex lg:hidden w-full max-w-md gap-3 animate-rise" style={{ animationDelay: "0.35s" }}>
              <button
                data-testid="quickpill-trading"
                onClick={() => navigate("/trading")}
                className="glass-card rounded-2xl flex-1 flex flex-col items-center gap-1.5 py-4 text-slate-100 active:scale-95 transition-transform"
              >
                <TrendingUp className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
                <span className="text-xs font-medium">Trading</span>
              </button>
              <button
                data-testid="quickpill-vision"
                onClick={() => navigate("/vision")}
                className="glass-card rounded-2xl flex-1 flex flex-col items-center gap-1.5 py-4 text-slate-100 active:scale-95 transition-transform"
              >
                <Eye className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
                <span className="text-xs font-medium">Vision</span>
              </button>
              <button
                data-testid="quickpill-plus"
                onClick={() => setMobileMenuOpen(true)}
                className="glass-card rounded-2xl flex-1 flex flex-col items-center gap-1.5 py-4 text-slate-100 active:scale-95 transition-transform"
              >
                <MoreHorizontal className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
                <span className="text-xs font-medium">Plus</span>
              </button>
            </div>

            {/* one-click confirmation (prudent mode) */}
            {awaitingConfirm && !busy && (
              <div
                data-testid="confirm-action-bar"
                className="flex items-center gap-2 rounded-xl border border-cyan-400/60 bg-cyan-500/10 px-3 py-2"
              >
                <span className="font-jb text-[11px] uppercase tracking-[0.15em] text-slate-200">
                  {pendingLabel || "Action proposée"}
                </span>
                <button
                  type="button"
                  data-testid="confirm-action-button"
                  onClick={() => { setAwaitingConfirm(false); handleSend("Oui, vas-y, je confirme."); }}
                  className="ml-auto rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 px-3.5 py-1.5 text-xs font-bold text-white"
                >
                  Confirmer
                </button>
                <button
                  type="button"
                  data-testid="cancel-action-button"
                  onClick={() => setAwaitingConfirm(false)}
                  className="rounded-lg border border-cyan-500/20 px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-white"
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        </main>

        {/* composer */}
        <footer data-testid="assistant-composer" className="shrink-0 px-3 sm:px-5 pt-2 pb-3 glass-topbar border-t">
          <div className="mx-auto w-full max-w-2xl">
            <VoiceControlBar
              state={state}
              busy={busy}
              recogSupported={recogSupported}
              handsFree={handsFree}
              micReady={micReady}
              onToggleHandsFree={toggleHandsFree}
              whisperDefault={whisperDefault}
              onSend={handleSend}
              onSendFile={handleSendFile}
              recognitionLang={recognitionLang}
              onSetLang={onSetLang}
              whisperRecording={whisperRecording}
              onWhisperToggle={onWhisperToggle}
              imgSize={imgSize}
              imgRatio={imgRatio}
              onSetImgSize={setImgSize}
              onSetImgRatio={setImgRatio}
              pendingImage={pendingImage}
              onAnalyzeImage={analyzePendingImage}
              onEditImage={editPendingImage}
              onClearPending={() => setPendingImage(null)}
            />
            <LinksBar
              links={openedLinks}
              onOpen={openLink}
              onRemove={(id) => setOpenedLinks((prev) => prev.filter((l) => l.id !== id))}
              onClear={() => setOpenedLinks([])}
            />
          </div>
        </footer>

        <AppNav variant="bottom" onOpenSettings={() => setSettingsOpen(true)} onOpenWeb={() => setBrowserOpen(true)} onOpenMenu={() => setMobileMenuOpen(true)} />
      </section>

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} voices={voices} />
      <SkillsDrawer open={skillsOpen} onClose={() => setSkillsOpen(false)} />
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        transcript={transcript}
        interim={interim}
        listening={listening}
        onClear={clearChat}
      />
      <MemoryDrawer open={memoryOpen} onClose={() => setMemoryOpen(false)} memories={memories} onDelete={deleteMemory} />
      <BrowserPreview open={browserOpen} onClose={() => setBrowserOpen(false)} />
      <MobileMenuSheet
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onHistory={() => setHistoryOpen(true)}
        onMemory={() => setMemoryOpen(true)}
        onSkills={() => setSkillsOpen(true)}
        onCast={() => window.open(`${window.location.origin}/tv`, "_blank", "noopener")}
        onSettings={() => setSettingsOpen(true)}
        onLogout={logout}
      />
    </div>
  );
}
